import React from 'react';
import Icon from '../../../components/AppIcon';
import { Field, inputClass } from './BackgroundFields';
import { uid } from '../utils/defaults';
import { normalizeSiteTypography } from '../utils/siteFonts';

export default function SiteCustomFontsPanel({ typography, onChange }) {
  const normalized = normalizeSiteTypography(typography);
  const { customFonts, defaultFontId, applyToSite } = normalized;

  const patchTypography = (patch) => {
    onChange(normalizeSiteTypography({ ...typography, ...patch }));
  };

  const syncLegacyPrimary = (fonts, primaryId) => {
    const primary = fonts.find((f) => f.id === primaryId) || fonts[0];
    return {
      customFonts: fonts,
      defaultFontId: primary?.id || null,
      fontFamily: primary?.fontFamily || '',
      fontUrl: primary?.fontUrl || '',
    };
  };

  const addFontFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const baseName = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
      const entry = {
        id: uid('font'),
        label: baseName || 'Police',
        fontFamily: baseName || 'Police',
        fontUrl: reader.result,
      };
      const nextFonts = [...customFonts, entry];
      patchTypography(
        syncLegacyPrimary(nextFonts, defaultFontId || entry.id),
      );
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const updateFont = (id, patch) => {
    const nextFonts = customFonts.map((f) => (f.id === id ? { ...f, ...patch } : f));
    patchTypography(syncLegacyPrimary(nextFonts, defaultFontId));
  };

  const removeFont = (id) => {
    const nextFonts = customFonts.filter((f) => f.id !== id);
    const nextDefault = defaultFontId === id ? nextFonts[0]?.id || null : defaultFontId;
    patchTypography(syncLegacyPrimary(nextFonts, nextDefault));
  };

  return (
    <div className="rounded-lg border border-border p-3 space-y-3 bg-muted/10">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-foreground">Polices personnalisées</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Importez autant de fichiers que nécessaire (.woff, .woff2, .ttf, .otf). Elles seront
            proposées dans le menu, les blocs texte et l&apos;éditeur riche.
          </p>
        </div>
      </div>

      <Field label="Ajouter une police">
        <input
          type="file"
          accept=".woff,.woff2,.ttf,.otf,font/woff,font/woff2,font/ttf,font/otf"
          onChange={addFontFile}
          className="text-sm"
        />
      </Field>

      {customFonts.length ? (
        <ul className="space-y-2">
          {customFonts.map((font) => (
            <li
              key={font.id}
              className="rounded-lg border border-border bg-background p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <label className="flex items-center gap-2 shrink-0 pt-2 cursor-pointer">
                  <input
                    type="radio"
                    name="site-default-font"
                    checked={defaultFontId === font.id}
                    onChange={() =>
                      patchTypography(syncLegacyPrimary(customFonts, font.id))
                    }
                  />
                  <span className="text-[10px] uppercase text-muted-foreground">Défaut</span>
                </label>
                <div className="flex-1 min-w-0 space-y-2">
                  <Field label="Nom affiché">
                    <input
                      className={inputClass}
                      value={font.label || ''}
                      onChange={(e) => updateFont(font.id, { label: e.target.value })}
                    />
                  </Field>
                  <Field label="Nom CSS (@font-face)">
                    <input
                      className={inputClass}
                      value={font.fontFamily || ''}
                      onChange={(e) => updateFont(font.id, { fontFamily: e.target.value })}
                    />
                  </Field>
                  <p
                    className="text-sm rounded-md border border-border px-2 py-1.5 truncate"
                    style={{ fontFamily: `"${font.fontFamily}", sans-serif` }}
                  >
                    Aperçu — {font.label || font.fontFamily}
                  </p>
                </div>
                <button
                  type="button"
                  className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive shrink-0"
                  title="Supprimer"
                  onClick={() => removeFont(font.id)}
                >
                  <Icon name="Trash2" size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">Aucune police importée.</p>
      )}

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={applyToSite === true}
          onChange={(e) => patchTypography({ applyToSite: e.target.checked })}
          disabled={!customFonts.length}
        />
        <span>
          <span className="text-sm font-medium text-foreground block">Appliquer à toute la page</span>
          <span className="text-xs text-muted-foreground">
            Utilise la police marquée « Défaut » sur toute la page (les blocs peuvent choisir une
            autre police).
          </span>
        </span>
      </label>
    </div>
  );
}
